import { AllAccountsFilterParams, AllAccountsResponse, ConsumerPlanConfigParams, ConsumerPlanConfigResponse, ConsumerDetailParams, ConsumerDetailResponse, ConsumerBillParams, ConsumerBillResponse, UtilityConfigFilters, UtilityRequestConfiguration, MultipleConsumerBillsParams } from "./types";
import { ApiEndpoints } from "@shared/api/api-endpoints";
import { cxApiClient, communicationApiClient, bxApiClient } from "@shared/api/clients/apiClientFactory";

export const sharedApi = {
  getAllAccounts: async (
    filters: AllAccountsFilterParams
  ): Promise<AllAccountsResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "nsc-add",
      (query) => {
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.active_consumers !== undefined) query.push("active_consumers", filters.active_consumers.toString());
        if (filters.remote_utility_id) query.push("remote_utility_id", filters.remote_utility_id.toString());
        if (filters.is_sub_account !== undefined) query.push("is_sub_account", filters.is_sub_account.toString());
        if (filters.consumer_id) query.push("consumer_id", filters.consumer_id.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.status && filters.status !== "all") query.push("status", filters.status);
        if (filters.utilityService && filters.utilityService !== "all") query.push("utility_service", filters.utilityService);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  getConsumerPlanConfig: async (
    params: ConsumerPlanConfigParams
  ): Promise<ConsumerPlanConfigResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "consumerWeb",
      "consumer-plan-config",
      (query) => {
        if (params.consumer_id) query.push("consumer_id", params.consumer_id.toString());
        if (params.remote_utility_id) query.push("remote_utility_id", params.remote_utility_id.toString());
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  getConsumerDetail: async (
    params: ConsumerDetailParams
  ): Promise<ConsumerDetailResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "consumerWeb",
      "consumer-detail",
      (query) => {
        if (params.remote_utility_id) query.push("remote_utility_id", params.remote_utility_id.toString());
        if (params.consumer_no) query.push("consumer_no", params.consumer_no);
        if (params.consumer_id) query.push("consumer_id", params.consumer_id);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  getConsumerBill: async (params: ConsumerBillParams): Promise<ConsumerBillResponse> => {
      const url = ApiEndpoints.createUrlWithQueryParameters(
        "consumerWeb",
        "consumer-bill",
        (query) => {
          if (params.remote_utility_id) query.push("remote_utility_id", params.remote_utility_id.toString());
          if (params.consumer_number) query.push("consumer_number", params.consumer_number);
          if (params.is_pagination_required !== undefined) query.push("is_pagination_required", String(params.is_pagination_required));
          if (params.is_bill_summary !== undefined) query.push("is_bill_summary", String(params.is_bill_summary));
          return query;
        }
      );
  
      const response = await cxApiClient.get(url);
      return response.data.result || {};
    },
    
  getMultipleConsumerBills: async (params: MultipleConsumerBillsParams): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "consumerWeb",
      "multiple-consumer-bills",
      (query) => {
        if (params.remote_utility_id) query.push("remote_utility_id", params.remote_utility_id.toString());
        if (Array.isArray(params.consumer_no)) {
          params.consumer_no.forEach(no => query.push("consumer_no", no));
        } else if (params.consumer_no) {
          query.push("consumer_no", params.consumer_no);
        }
        if (params.is_pagination_required !== undefined) query.push("is_pagination_required", String(params.is_pagination_required));
        if (params.is_bill_summary !== undefined) query.push("is_bill_summary", String(params.is_bill_summary));
        if (params.payment_status) query.push("payment_status", params.payment_status);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return response.data?.result || {};
  },
    
   getUtilityRequestConfiguration: async (
    filters: UtilityConfigFilters
   ): Promise<{ result: UtilityRequestConfiguration[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
    "consumerWeb",
    "utility-request-configuration",
     (query) => {
      query.push("remote_utility_id", filters.remote_utility_id);
      query.push("request_type", filters.requestType);
      if (filters.disablePagination) query.push("disable_pagination","true");
      if (filters.searchData) query.push("search_data", filters.searchData);
      return query;
     }
    );
  
    const response = await cxApiClient.get(url);
    return response.data;
   },

   getTimeSlotChoices: async (): Promise<any> => {
    const url = ApiEndpoints.createUrl("consumerWeb", "choices/time_slot");
    const response = await cxApiClient.get(url);
    return response.data;
  },

  getAccountStatusChoices: async (): Promise<any> => {
    const url = ApiEndpoints.createUrl("cx", "choices/account_status");
    const response = await cxApiClient.get(url);
    return response.data;
  },

  getNotes: async (params: {
    remote_utility_id: string;
    request_id:any;
    source:string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "consumerWeb",
      "consumer-request/notes",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("request_id", params.request_id);
        qs.push("source", params.source);
      }
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  getActivityLog: async (params: {
    id: string;
    remote_utility_id: string;
    module:any
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "activityLog",
      `logs`,
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("request_id", params.id);
        qs.push("module", params.module);
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data;
  },

  getRequestDetail: async (params: {
    id: string;
    remote_utility_id: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "consumerWeb",
      `consumer-request/detail/${params.id}`,
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
      }
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  addNote: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrl("cx", "consumer-request/notes");
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  getPaymentList: async (params: {
    remote_utility_id?: string | number;
    consumer_id?: string | number | any[];
    page?: number | string;
    limit?: number | string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "consumerWeb",
      "payment",
      (query) => {
        if (params.remote_utility_id) query.push("remote_utility_id", params.remote_utility_id.toString());
        if (Array.isArray(params.consumer_id)) {
          // If the array is empty, we might not want to pass it, but if we do, loop through
          params.consumer_id.forEach(id => query.push("consumer_id", id.toString()));
        } else if (params.consumer_id) {
          query.push("consumer_id", params.consumer_id.toString());
        }
        if (params.page !== undefined) query.push("page", params.page.toString());
        if (params.limit !== undefined) query.push("limit", params.limit.toString());
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  downloadBillTemplate: async (params: { billId: string }): Promise<Blob> => {
    const response = await bxApiClient.get(
      `render-dynamic-template/${params.billId}/`,
      {
        responseType: "blob",
      }
    );
    return response.data;
  },
};
